import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { computeRoute } from '../../utils/routesApi';
import commandCenterService from '../../services/commandCenterService';
import placesService from '../../services/placesService';
import CommandCenterBookingForm from './CommandCenterBookingForm';
import CommandCenterAssignDriverCard from './CommandCenterAssignDriverCard';
import CommandCenterBookingMapCard from './CommandCenterBookingMapCard';
import CommandCenterAssignPricingPopup from './CommandCenterAssignPricingPopup';
import CommandCenterAutoAssignPopup from './CommandCenterAutoAssignPopup';
import CommandCenterWaitingForDriverPopup from './CommandCenterWaitingForDriverPopup';
import CommandCenterManualAssignPopup from './CommandCenterManualAssignPopup';
import { extractAddressDetails } from '../ui/LocationAutocomplete';
import { deriveTripStage } from './mockTrackingData';

const EMPTY_PRICE_FIELDS = {
  systemPrice: '',
  fleetOperatorPrice: '',
  platformServiceFee: '',
  fleetOperatorServiceFee: '',
  discount: '',
  cgst: '',
  sgst: '',
  totalPrice: '',
  offeredPrice: '',
  agreedPrice: '',
  partnerPrice: '',
};
const CANDIDATES_PAGE_SIZE = 20;
const DEFAULT_CANDIDATE_FILTERS = { maxEtaMinutes: 60, maxDistanceKm: 60, vehicleTypeNames: [], search: '', partnerOnly: false };
const EMPTY_DRIVERS_LIST = [];

const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const aLat = parseFloat(a.latitude ?? a.lat);
  const aLng = parseFloat(a.longitude ?? a.lng);
  const bLat = parseFloat(b.latitude ?? b.lat);
  const bLng = parseFloat(b.longitude ?? b.lng);
  if (isNaN(aLat) || isNaN(aLng) || isNaN(bLat) || isNaN(bLng)) return null;

  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export const areLocationsSame = (pickupCoords, dropoffCoords, pickupText, dropoffText) => {
  if (pickupCoords && dropoffCoords) {
    const dist = haversineKm(pickupCoords, dropoffCoords);
    if (dist != null && dist < 0.05) return true;
  }
  if (
    pickupText &&
    dropoffText &&
    typeof pickupText === 'string' &&
    typeof dropoffText === 'string' &&
    pickupText.trim().length > 0 &&
    pickupText.trim().toLowerCase() === dropoffText.trim().toLowerCase()
  ) {
    return true;
  }
  return false;
};

// Only fills a field the dispatcher hasn't already typed a value into —
// never overwrites prices the user entered themselves.
const fillEmptyPriceFields = (current, incoming) => {
  const next = { ...current };
  Object.entries(incoming).forEach(([key, value]) => {
    if (value === '' || value == null) return;
    if (current[key] === '' || current[key] == null) {
      next[key] = value;
    }
  });
  return next;
};

// Maps a real backend candidate (commandCenterDispatchService.findCandidates)
// to the shape the Assign Driver card/popup/map already render.
const mapCandidateToDriver = (c) => ({
  id: c.vehicleId,
  driverId: c.driverId,
  vehicleId: c.vehicleId,
  name: c.driverName,
  phone: c.driverPhone,
  vehiclePlate: c.plateNumber,
  vehicleTypeLabel: c.vehicleTypeLabel,
  vendorName: c.vendorName,
  vendorPhone: c.vendorPhone,
  vendorCity: c.vendorCity,
  vendorArea: c.vendorArea,
  vendorPincode: c.vendorPincode,
  isPartnerFleet: c.isPartnerFleet,
  tripSummary: c.tripSummary || null,
  distanceKm: c.distanceKm,
  etaMinutes: c.etaMinutes,
  location: c.location,
  rating: null,
  estimatedFare: null,
});

// Auto Assign never surfaces its candidates to the frontend (the backend
// picks and cycles through them server-side), so once one is confirmed this
// maps the booking's own driver/vehicle includes into the same shape
// mapCandidateToDriver produces, for the map/driver-card to render.
const mapConfirmedBookingToDriver = (booking) => ({
  id: booking.vehicle?.vehicle_id,
  driverId: booking.driver?.driver_id,
  vehicleId: booking.vehicle?.vehicle_id,
  name: booking.driver?.name,
  phone: booking.driver?.phone,
  vehiclePlate: booking.vehicle?.plate_number,
  vehicleTypeLabel: booking.vehicle?.type?.name,
  vendorName: booking.vehicle?.operator?.name,
  vendorPhone: null,
  isPartnerFleet: false,
  distanceKm: null,
  etaMinutes: null,
  location: booking.vehicle?.current_latitude != null && booking.vehicle?.current_longitude != null
    ? { latitude: parseFloat(booking.vehicle.current_latitude), longitude: parseFloat(booking.vehicle.current_longitude) }
    : null,
  rating: null,
  estimatedFare: null,
});

// Inverse of buildBookingPayload — seeds CommandCenterBookingForm's internal
// formData from a real booking (Trips page Edit, driver-less bookings only).
const mapBookingToInitialFormData = (booking) => ({
  hospital: {
    id: booking.hospital_id || null,
    name: booking.hospital?.name || booking.hospital_name_text || '',
  },
  name: booking.patient_name || '',
  phone: booking.patient_phone || '',
  phone2: booking.contact_person_phone || '',
  age: booking.patient_age != null ? String(booking.patient_age) : '',
  gender: booking.patient_gender || '',
  height: booking.patient_height_cm != null ? String(booking.patient_height_cm) : '',
  weight: booking.patient_weight_kg != null ? String(booking.patient_weight_kg) : '',
  pickupText: booking.pickup_address || '',
  dropoffText: booking.dropoff_address || '',
  scheduleTime: booking.scheduled_datetime || '',
  patientCondition: booking.patient_condition || '',
  serviceType: booking.service_type || '',
  source: booking.source_channel || '',
  partnerShare: booking.partner_share_percent != null ? Number(booking.partner_share_percent) : 50,
  requirements: (booking.BookingRequirements || []).map((r) => r.equipment?.name).filter(Boolean),
  otherRequirement: booking.other_requirements || '',
  documentType: booking.document_type || '',
  documentFileName: booking.document_file_name || '',
  documentUrl: booking.document_url || '',
  vehicleType: booking.requested_vehicle_types?.[0] || booking.vehicle?.type?.name || '',
  vehicleTypes: booking.requested_vehicle_types || (booking.vehicle?.type?.name ? [booking.vehicle.type.name] : []),
});

// Only Name and Phone 1 are mandatory to create a Command Center booking —
// everything else (hospital, pickup/drop locations, patient condition,
// vehicle type, requirements, pricing, schedule) is optional.
const getMissingRequiredFields = (formData) => {
  const missing = [];
  if (!formData?.name?.trim()) missing.push('Patient Name');
  const phoneDigits = (formData?.phone || '').replace(/\D/g, '');
  if (!phoneDigits) {
    missing.push('Phone 1');
  } else if (phoneDigits.length !== 10) {
    missing.push('Valid 10-digit Phone 1');
  }
  return missing;
};

const buildBookingPayload = (formData, priceFields, pickupCoordinates, dropoffCoordinates) => ({
  // null (not undefined) on purpose — this payload is also used to PATCH an
  // already-created booking on every re-search (see ensureBooking), and
  // JSON.stringify drops undefined keys entirely, so a cleared hospital
  // would never reach the backend as an actual clear and the booking's old
  // hospital_id (and its partner badges) would silently keep showing.
  hospitalId: formData.hospital?.id || null,
  hospitalNameText: formData.hospital?.id ? null : (formData.hospital?.name?.trim() || null),
  patientName: formData.name?.trim() || undefined,
  patientPhone: formData.phone?.trim() || undefined,
  contactPersonPhone: formData.phone2?.trim() || undefined,
  patientAge: formData.age !== '' && formData.age != null ? Number(formData.age) : undefined,
  patientGender: formData.gender || undefined,
  patientHeightCm: formData.height !== '' && formData.height != null ? Number(formData.height) : undefined,
  patientWeightKg: formData.weight !== '' && formData.weight != null ? Number(formData.weight) : undefined,
  pickupAddress: formData.pickupText || undefined,
  pickupLatitude: pickupCoordinates?.latitude,
  pickupLongitude: pickupCoordinates?.longitude,
  dropoffAddress: formData.dropoffText || undefined,
  dropoffLatitude: dropoffCoordinates?.latitude,
  dropoffLongitude: dropoffCoordinates?.longitude,
  requestedVehicleTypes: (formData.vehicleTypes && formData.vehicleTypes.length > 0)
    ? formData.vehicleTypes
    : (formData.vehicleType ? [formData.vehicleType] : []),
  requirementNames: formData.requirements,
  otherRequirements: formData.otherRequirement || undefined,
  serviceType: formData.serviceType || undefined,
  sourceChannel: formData.source || undefined,
  patientCondition: formData.patientCondition || undefined,
  scheduledDatetime: formData.scheduleTime ? new Date(formData.scheduleTime).toISOString() : undefined,
  documentType: formData.documentType || undefined,
  documentFileName: formData.documentFileName || undefined,
  // systemPrice: priceFields.systemPrice !== '' ? Number(priceFields.systemPrice) : undefined, // commented out per request
  fleetOperatorPrice: (priceFields.fleetOperatorPrice ?? priceFields.offeredPrice) !== '' && (priceFields.fleetOperatorPrice ?? priceFields.offeredPrice) != null ? Number(priceFields.fleetOperatorPrice ?? priceFields.offeredPrice) : undefined,
  platformServiceFee: (priceFields.platformServiceFee ?? priceFields.agreedPrice) !== '' && (priceFields.platformServiceFee ?? priceFields.agreedPrice) != null ? Number(priceFields.platformServiceFee ?? priceFields.agreedPrice) : undefined,
  fleetOperatorServiceFee: (priceFields.fleetOperatorServiceFee ?? priceFields.partnerPrice) !== '' && (priceFields.fleetOperatorServiceFee ?? priceFields.partnerPrice) != null ? Number(priceFields.fleetOperatorServiceFee ?? priceFields.partnerPrice) : undefined,
  discount: (priceFields.discount !== '' && priceFields.discount != null) ? Number(priceFields.discount) : undefined,
  cgst: (priceFields.cgst !== '' && priceFields.cgst != null) ? Number(priceFields.cgst) : undefined,
  sgst: (priceFields.sgst !== '' && priceFields.sgst != null) ? Number(priceFields.sgst) : undefined,
  totalPrice: priceFields.totalPrice !== '' && priceFields.totalPrice != null ? Number(priceFields.totalPrice) : undefined,
  offeredPrice: (priceFields.offeredPrice ?? priceFields.fleetOperatorPrice) !== '' && (priceFields.offeredPrice ?? priceFields.fleetOperatorPrice) != null ? Number(priceFields.offeredPrice ?? priceFields.fleetOperatorPrice) : undefined,
  agreedPrice: (priceFields.agreedPrice ?? priceFields.platformServiceFee) !== '' && (priceFields.agreedPrice ?? priceFields.platformServiceFee) != null ? Number(priceFields.agreedPrice ?? priceFields.platformServiceFee) : undefined,
  partnerPrice: (priceFields.partnerPrice ?? priceFields.fleetOperatorServiceFee) !== '' && (priceFields.partnerPrice ?? priceFields.fleetOperatorServiceFee) != null ? Number(priceFields.partnerPrice ?? priceFields.fleetOperatorServiceFee) : undefined,
  // partnerSharePercent: formData.partnerShare, // Disabled while slider logic is hidden; preserved for future use
});

// Composes the redesigned booking page: a scrolling form on the left, and a
// sticky "assign driver" + "map" stack on the right that stays in place while
// the form grows/scrolls.
const CommandCenterBookingPage = ({ editBookingId, onAction, onError, onNavigateToTrips }) => {
  const [loadingEditBooking, setLoadingEditBooking] = useState(!!editBookingId);
  const [editInitialFormData, setEditInitialFormData] = useState(null);
  const [pickupCoordinates, setPickupCoordinates] = useState(null);
  const [dropoffCoordinates, setDropoffCoordinates] = useState(null);
  const [assignedDriver, setAssignedDriver] = useState(null);
  const [driverCardExpanded, setDriverCardExpanded] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [savingLater, setSavingLater] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [priceFields, setPriceFields] = useState(EMPTY_PRICE_FIELDS);
  const [pricingPopup, setPricingPopup] = useState(null); // null | { mode: 'driver', driver } | { mode: 'manual' }
  const [autoAssignPopup, setAutoAssignPopup] = useState(false);
  const [route, setRoute] = useState({ path: [], distanceKm: null, durationMin: null });
  const [bookingId, setBookingId] = useState(null);
  const [activeBooking, setActiveBooking] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [candidateFilters, setCandidateFilters] = useState(DEFAULT_CANDIDATE_FILTERS);
  const [searchedHospital, setSearchedHospital] = useState(null);
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState([]);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [waitingForDriverPopup, setWaitingForDriverPopup] = useState(null); // null | { driver }
  const [manualAssignResult, setManualAssignResult] = useState(null); // null | { phase: 'loading'|'success'|'error', booking?, driver?, errorMessage? }
  const [locationFilters, setLocationFilters] = useState({ city: '', area: '', pincode: '' });
  const [activeGeocodedLocation, setActiveGeocodedLocation] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Auto-populate city, area, pincode into locationFilters when pickup location is selected
  const handlePickupCoordinatesChange = (location) => {
    setPickupCoordinates(location);
    if (location && (location.city || location.area || location.pincode)) {
      setLocationFilters((prev) => ({
        ...prev,
        city: location.city || prev.city,
        area: location.area || prev.area,
        pincode: location.pincode || prev.pincode,
      }));
    }
  };
  // In-flight createBooking promise, or null — see ensureBooking below. A ref,
  // not state, because it has to be readable synchronously on the very next
  // call (a rapid double-click fires before React would ever re-render with
  // an updated bookingId state).
  const creatingBookingRef = useRef(null);
  // Draft patient-location link sent from the form before the booking exists. Sent once with createBooking
  // (never with the PATCH updates, which don't accept it) so the backend attaches it to the new booking.
  const locationShareIdRef = useRef(null);
  // Same reasoning as creatingBookingRef, for the PATCH path — once bookingId
  // already exists, a rapid double-click on Save Later/Search Ambulance would
  // otherwise fire two concurrent updateBooking calls for the same booking.
  const updatingBookingRef = useRef(null);
  // Set right before the edit-load effect below seeds pickupCoordinates from
  // an existing booking, so the reset effect immediately after it doesn't
  // wipe the bookingId/etc it just set — see that effect's comment.
  const skipNextPickupResetRef = useRef(false);
  // Synchronous re-entrancy guards for the Search Ambulance / Save Later
  // handlers themselves — the `searching`/`savingLater` STATE below only
  // disables the buttons visually (and only takes effect after a render), so
  // a double-click landing within the same tick could still fire the handler
  // twice before React ever applies `disabled`. These refs are checked and
  // set synchronously, closing that gap; ensureBooking's own ref guards
  // above are the second, independent layer that keeps the actual network
  // calls deduped even if a handler somehow still ran twice.
  const searchInFlightRef = useRef(false);
  const saveLaterInFlightRef = useRef(false);
  const updatingInFlightRef = useRef(false);
  const currentFormDataRef = useRef(null);
  const pickupGeocodeTimerRef = useRef(null);

  // Check if pickup and dropoff locations are identical
  const isSameLocation = areLocationsSame(pickupCoordinates, dropoffCoordinates);

  // If dropoff changes to match pickup, immediately clear stale candidates and routes
  useEffect(() => {
    if (isSameLocation) {
      setAssignedDriver(null);
      setHasSearched(false);
      setBookingId(null);
      setActiveBooking(null);
      setDrivers([]);
      setCandidatesTotal(0);
      setRoute({ path: [], distanceKm: null, durationMin: null });
      creatingBookingRef.current = null;
      setActiveGeocodedLocation(null);
      setLocationFilters({ city: '', area: '', pincode: '' });
    }
  }, [isSameLocation]);

  // When pickup coordinates change: if coordinates are valid, automatically
  // search nearby ambulances starting from those coordinates.
  useEffect(() => {
    if (skipNextPickupResetRef.current) {
      skipNextPickupResetRef.current = false;
      return undefined;
    }
    setAssignedDriver(null);
    setBookingId(null);
    setActiveBooking(null);
    creatingBookingRef.current = null;
    setActiveGeocodedLocation(null);

    // Auto-fill locationFilters if pickupCoordinates has parsed address details
    if (pickupCoordinates && (pickupCoordinates.city || pickupCoordinates.area || pickupCoordinates.pincode)) {
      setLocationFilters((prev) => ({
        city: pickupCoordinates.city || prev.city || '',
        area: pickupCoordinates.area || prev.area || '',
        pincode: pickupCoordinates.pincode || prev.pincode || '',
      }));
    } else if (!pickupCoordinates) {
      setLocationFilters({ city: '', area: '', pincode: '' });
    } else if (pickupCoordinates.latitude && pickupCoordinates.longitude) {
      // Reverse geocode fallback to fill city/area/pincode if not present
      placesService.reverseGeocode(pickupCoordinates.latitude, pickupCoordinates.longitude)
        .then((res) => {
          if (res?.success && res.data) {
            const parsed = extractAddressDetails(
              res.data.addressComponents,
              res.data.formattedAddress || res.data.address,
              res.data.name
            );
            if (parsed.city || parsed.area || parsed.pincode) {
              setLocationFilters((prev) => ({
                city: parsed.city || prev.city || '',
                area: parsed.area || prev.area || '',
                pincode: parsed.pincode || prev.pincode || '',
              }));
            }
          }
        })
        .catch(() => {});
    }

    if (!pickupCoordinates?.latitude || !pickupCoordinates?.longitude) {
      setHasSearched(false);
      setDrivers([]);
      setCandidatesTotal(0);
      return undefined;
    }

    setHasSearched(true);
    setSearching(true);
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const activeVehicleTypes = (currentFormDataRef.current?.vehicleTypes?.length > 0)
          ? currentFormDataRef.current.vehicleTypes
          : (currentFormDataRef.current?.vehicleType ? [currentFormDataRef.current.vehicleType] : selectedVehicleTypes);
        const initialFilters = {
          ...candidateFilters,
          vehicleTypeNames: activeVehicleTypes || [],
          partnerOnly: currentFormDataRef.current?.hospital?.id ? candidateFilters.partnerOnly : false,
        };
        setCandidateFilters(initialFilters);
        const { candidates, total } = await fetchCandidatesPage(null, initialFilters, 0);
        if (cancelled) return;
        setDrivers(candidates);
        setCandidatesTotal(total);
      } catch (err) {
        if (!cancelled) {
          onError?.(err.message || 'Failed to search nearby ambulances');
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pickupCoordinates?.latitude, pickupCoordinates?.longitude]);

  // Loads an existing booking for editing (Trips page Edit, driver-less
  // bookings only — see CommandCenterTrackingSimulator's `!trip.driver`
  // gate). Sets skipNextPickupResetRef right before setPickupCoordinates so
  // the reset effect above — which normally clears bookingId whenever
  // pickup changes — doesn't immediately wipe out the bookingId this effect
  // just set.
  useEffect(() => {
    if (!editBookingId) {
      setLoadingEditBooking(false);
      setEditInitialFormData(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingEditBooking(true);
    commandCenterService.getBooking(editBookingId)
      .then((result) => {
        if (cancelled) return;
        const booking = result.data;
        setBookingId(booking.booking_id);
        setActiveBooking(booking);
        skipNextPickupResetRef.current = true;
        setPickupCoordinates(booking.pickup_latitude != null
          ? { latitude: parseFloat(booking.pickup_latitude), longitude: parseFloat(booking.pickup_longitude) }
          : null);
        setDropoffCoordinates(booking.dropoff_latitude != null
          ? { latitude: parseFloat(booking.dropoff_latitude), longitude: parseFloat(booking.dropoff_longitude) }
          : null);
        setPriceFields({
          systemPrice: booking.system_price ?? '',
          fleetOperatorPrice: booking.fleet_operator_price ?? booking.offered_price ?? '',
          platformServiceFee: booking.platform_service_fee ?? booking.agreed_price ?? '',
          fleetOperatorServiceFee: booking.fleet_operator_service_fee ?? booking.partner_price ?? '',
          discount: booking.discount ?? '',
          cgst: booking.cgst ?? '',
          sgst: booking.sgst ?? '',
          totalPrice: booking.total_price ?? booking.estimated_fare ?? '',
        });
        setSelectedVehicleTypes(booking.requested_vehicle_types || []);
        if (booking.driver) {
          setAssignedDriver(mapConfirmedBookingToDriver(booking));
        }
        setEditInitialFormData(mapBookingToInitialFormData(booking));
      })
      .catch((error) => {
        if (!cancelled) {
          onError?.(error.message || 'Failed to load booking for editing');
          setEditInitialFormData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingEditBooking(false);
      });
    return () => { cancelled = true; };
    // onError intentionally omitted — same reasoning as the price-estimate
    // effect below (new identity every render, would re-trigger this fetch).
  }, [editBookingId]);

  // Compute the route once both pickup and dropoff are set — same Routes API
  // helper used by the map/tracking components, with a straight-line fallback.
  useEffect(() => {
    if (!pickupCoordinates || !dropoffCoordinates || isSameLocation) {
      setRoute({ path: [], distanceKm: null, durationMin: null });
      return undefined;
    }

    const pLat = parseFloat(pickupCoordinates.latitude ?? pickupCoordinates.lat);
    const pLng = parseFloat(pickupCoordinates.longitude ?? pickupCoordinates.lng);
    const dLat = parseFloat(dropoffCoordinates.latitude ?? dropoffCoordinates.lat);
    const dLng = parseFloat(dropoffCoordinates.longitude ?? dropoffCoordinates.lng);

    if (isNaN(pLat) || isNaN(pLng) || isNaN(dLat) || isNaN(dLng)) {
      setRoute({ path: [], distanceKm: null, durationMin: null });
      return undefined;
    }

    const origin = { lat: pLat, lng: pLng };
    const destination = { lat: dLat, lng: dLng };
    const straightLineKm = haversineKm({ latitude: pLat, longitude: pLng }, { latitude: dLat, longitude: dLng });
    let cancelled = false;

    computeRoute(origin, destination)
      .then(({ coordinates, distance, duration }) => {
        if (cancelled) return;
        if (coordinates && coordinates.length > 0) {
          setRoute({ path: coordinates, distanceKm: distance / 1000, durationMin: duration / 60 });
        } else {
          setRoute({ path: [origin, destination], distanceKm: straightLineKm, durationMin: (straightLineKm / 30) * 60 });
        }
      })
      .catch((err) => {
        console.warn('computeRoute failed, falling back to straight-line route:', err);
        if (!cancelled) {
          setRoute({ path: [origin, destination], distanceKm: straightLineKm, durationMin: (straightLineKm / 30) * 60 });
        }
      });

    return () => { cancelled = true; };
  }, [pickupCoordinates, dropoffCoordinates, isSameLocation]);

  // Auto-calculate "System Price" once pickup+dropoff are both set, and
  // again whenever the selected ambulance type changes (only one can be
  // selected now — a radio, not a checklist) — same debounce as the
  // Hospital booking form's fare estimation. vehicleTypeName narrows the
  // quote to that specific type's real fares instead of a blanket median
  // across every type, so switching types must re-fetch, not just re-filter
  // something already in hand.
  // System Price estimation logic — commented out per request
  /*
  useEffect(() => {
    if (!pickupCoordinates || !dropoffCoordinates || isSameLocation) {
      setPriceFields((prev) => (prev.systemPrice !== '' ? { ...prev, systemPrice: '' } : prev));
      return undefined;
    }
    let cancelled = false;
    setCalculatingPrice(true);
    const timeoutId = setTimeout(async () => {
      try {
        const result = await commandCenterService.estimateFare({
          pickupLatitude: pickupCoordinates.latitude,
          pickupLongitude: pickupCoordinates.longitude,
          dropoffLatitude: dropoffCoordinates.latitude,
          dropoffLongitude: dropoffCoordinates.longitude,
          vehicleTypeName: selectedVehicleTypes[0] || undefined,
        });
        if (cancelled) return;
        if (result.data?.systemPrice != null) {
          setPriceFields((prev) => ({ ...prev, systemPrice: result.data.systemPrice }));
        }
      } catch (error) {
        if (!cancelled) onError?.(error.message || 'Failed to calculate system price');
      } finally {
        if (!cancelled) setCalculatingPrice(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [pickupCoordinates?.latitude, pickupCoordinates?.longitude, dropoffCoordinates?.latitude, dropoffCoordinates?.longitude, selectedVehicleTypes.join(',')]);
  */

  // Ensures a booking exists on the backend and reflects the CURRENT form
  // state. Creates one on the first search; on every search after that it
  // PATCHes the existing booking instead of no-op'ing — findCandidates reads
  // hospital_id/pickup coordinates straight from that row, so without this,
  // editing the form after an initial search (e.g. picking a hospital, or
  // clearing one) would never reach the backend and the candidate list/
  // partner badges would keep reflecting whatever was true at creation time.
  const ensureBooking = async (formData) => {
    const isSame = isSameLocation || areLocationsSame(
      pickupCoordinates,
      dropoffCoordinates,
      formData?.pickupText,
      formData?.dropoffText
    );
    if (isSame) {
      throw new Error('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
    }
    if (formData.scheduleTime) {
      const scheduledTime = new Date(formData.scheduleTime);
      if (scheduledTime < new Date()) {
        throw new Error('Scheduled time cannot be in the past. Please select a present or future date and time.');
      }
    }
    const payload = buildBookingPayload(formData, priceFields, pickupCoordinates, dropoffCoordinates);
    if (bookingId) {
      // Same double-click race as the create path below, one call later — a
      // rapid second click on Search/Save Later while the first PATCH is
      // still in flight just awaits that same call instead of firing another.
      if (updatingBookingRef.current) {
        await updatingBookingRef.current;
        return bookingId;
      }
      const updatePromise = commandCenterService.updateBooking(bookingId, payload)
        .then((result) => {
          if (result?.data) setActiveBooking(result.data);
          return result;
        })
        .finally(() => { updatingBookingRef.current = null; });
      updatingBookingRef.current = updatePromise;
      await updatePromise;
      return bookingId;
    }
    // A rapid double-click on Search/Save Later can call this a second time
    // before the first createBooking response has landed and set bookingId
    // (the check above still sees null) — without this, that race creates
    // two separate booking rows for the same submit. Whoever gets here while
    // a creation is already in flight just awaits that SAME call instead of
    // starting a new one.
    if (creatingBookingRef.current) {
      return creatingBookingRef.current;
    }
    const creationPromise = (async () => {
      try {
        const result = await commandCenterService.createBooking(
          locationShareIdRef.current ? { ...payload, locationShareId: locationShareIdRef.current } : payload
        );
        const newBookingId = result.data.booking_id;
        setBookingId(newBookingId);
        setActiveBooking(result.data);
        return newBookingId;
      } finally {
        creatingBookingRef.current = null;
      }
    })();
    creatingBookingRef.current = creationPromise;
    return creationPromise;
  };

  // Fetches one page of candidates for the given offset — 20 at a time
  // (CANDIDATES_PAGE_SIZE), never the whole fleet in one response (thousands
  // of vehicles rendered at once is what was freezing the page). partnerFirst
  // is always requested so partner-fleet candidates sort to the top; the
  // card's "Partner" checkbox drives partnerOnly, a real server-side filter
  // that requires a fresh page-1 fetch (see the effect above), not a
  // client-side re-sort of whatever page happened to already be loaded.
  const fetchCandidatesPage = async (activeBookingId, filters, offset, customCoords = null) => {
    const coords = customCoords || activeGeocodedLocation || pickupCoordinates;
    if (activeBookingId && !customCoords && !activeGeocodedLocation) {
      const result = await commandCenterService.getCandidates(activeBookingId, {
        maxEtaMinutes: filters.maxEtaMinutes,
        maxDistanceKm: filters.maxDistanceKm,
        vehicleTypeNames: filters.vehicleTypeNames,
        search: filters.search || undefined,
        city: locationFilters?.city || undefined,
        area: locationFilters?.area || undefined,
        pincode: locationFilters?.pincode || undefined,
        partnerFirst: true,
        partnerOnly: filters.partnerOnly || undefined,
        limit: CANDIDATES_PAGE_SIZE,
        offset,
      });
      return {
        candidates: (result.data?.candidates || []).map(mapCandidateToDriver),
        total: result.data?.total ?? 0,
      };
    } else if (coords?.latitude != null && coords?.longitude != null) {
      const result = await commandCenterService.searchCandidatesByLocation({
        pickupLatitude: coords.latitude,
        pickupLongitude: coords.longitude,
        hospitalId: currentFormDataRef.current?.hospital?.id || undefined,
        maxEtaMinutes: filters.maxEtaMinutes,
        maxDistanceKm: filters.maxDistanceKm,
        vehicleTypeNames: filters.vehicleTypeNames,
        search: filters.search || undefined,
        city: locationFilters?.city || undefined,
        area: locationFilters?.area || undefined,
        pincode: locationFilters?.pincode || undefined,
        partnerFirst: true,
        partnerOnly: filters.partnerOnly || undefined,
        limit: CANDIDATES_PAGE_SIZE,
        offset,
      });
      return {
        candidates: (result.data?.candidates || []).map(mapCandidateToDriver),
        total: result.data?.total ?? 0,
      };
    }
    return { candidates: [], total: 0 };
  };

  const handleSearchByLocation = async () => {
    const { city, area, pincode } = locationFilters;
    const parts = [area?.trim(), city?.trim(), pincode?.trim()].filter(Boolean);
    if (parts.length === 0) {
      onError?.('Please enter at least City, Area, or Pincode to search');
      return;
    }

    setSearching(true);
    setIsGeocoding(true);
    try {
      const activeVehicleTypes = (currentFormDataRef.current?.vehicleTypes?.length > 0)
        ? currentFormDataRef.current.vehicleTypes
        : (currentFormDataRef.current?.vehicleType ? [currentFormDataRef.current.vehicleType] : selectedVehicleTypes);

      const filters = {
        ...candidateFilters,
        vehicleTypeNames: activeVehicleTypes || [],
      };

      // Always geocode the entered City / Area / Pincode using Google Places to get its real coordinates.
      // This DOES NOT change the booking's pickupCoordinates, so the map pickup marker stays in place.
      const query = parts.join(', ');
      let targetCoords = null;
      try {
        const geoRes = await placesService.geocodeAddress(query);
        const locData = geoRes?.data?.location || geoRes?.location;
        const lat = locData?.latitude ?? locData?.lat;
        const lng = locData?.longitude ?? locData?.lng;
        if (lat != null && lng != null) {
          targetCoords = {
            latitude: Number(lat),
            longitude: Number(lng),
            label: parts.join(', '),
            formattedAddress: geoRes?.data?.formattedAddress || geoRes?.formattedAddress || query,
          };
        }
      } catch (geoErr) {
        console.warn('Geocoding search query failed:', geoErr.message);
      }

      if (!targetCoords && pickupCoordinates?.latitude != null && pickupCoordinates?.longitude != null) {
        targetCoords = {
          ...pickupCoordinates,
          label: parts.join(', '),
        };
      }

      if (!targetCoords?.latitude || !targetCoords?.longitude) {
        throw new Error(`Unable to resolve coordinates for "${query}". Please check the spelling or enter a pickup location.`);
      }

      setActiveGeocodedLocation(targetCoords);

      const { candidates, total } = await fetchCandidatesPage(null, filters, 0, targetCoords);
      setDrivers(candidates);
      setCandidatesTotal(total);
      setHasSearched(true);
      onAction?.(`Found ${total} available ambulances near ${parts.join(', ')}`);
    } catch (err) {
      onError?.(err.message || 'Failed to search ambulances');
    } finally {
      setIsGeocoding(false);
      setSearching(false);
    }
  };

  const handleClearLocationSearch = async () => {
    setLocationFilters({ city: '', area: '', pincode: '' });
    setActiveGeocodedLocation(null);

    if (pickupCoordinates?.latitude != null && pickupCoordinates?.longitude != null) {
      setSearching(true);
      try {
        const activeVehicleTypes = (currentFormDataRef.current?.vehicleTypes?.length > 0)
          ? currentFormDataRef.current.vehicleTypes
          : (currentFormDataRef.current?.vehicleType ? [currentFormDataRef.current.vehicleType] : selectedVehicleTypes);

        const filters = {
          ...candidateFilters,
          vehicleTypeNames: activeVehicleTypes || [],
        };
        const { candidates, total } = await fetchCandidatesPage(bookingId, filters, 0, pickupCoordinates);
        setDrivers(candidates);
        setCandidatesTotal(total);
        onAction?.('Reset back to booking pickup location');
      } catch (err) {
        onError?.(err.message || 'Failed to reload ambulances');
      } finally {
        setSearching(false);
      }
    } else {
      setDrivers([]);
      setCandidatesTotal(0);
      setHasSearched(false);
    }
  };

  const handleVehicleTypeChange = (types) => {
    const list = Array.isArray(types) ? types : (types ? [types] : []);
    setSelectedVehicleTypes(list);
    setCandidateFilters((prev) => ({
      ...prev,
      vehicleTypeNames: list,
    }));
  };

  const handleSearchAmbulance = async (formData) => {
    const missing = getMissingRequiredFields(formData, pickupCoordinates, dropoffCoordinates);
    if (missing.length > 0) {
      onError?.(`Please fill in the required fields: ${missing.join(', ')}`);
      return;
    }
    const isSame = isSameLocation || areLocationsSame(
      pickupCoordinates,
      dropoffCoordinates,
      formData?.pickupText,
      formData?.dropoffText
    );
    if (isSame) {
      onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
      return;
    }
    if (formData.scheduleTime) {
      const scheduledTime = new Date(formData.scheduleTime);
      if (scheduledTime < new Date()) {
        onError?.('Scheduled time cannot be in the past. Please select a present or future date and time.');
        return;
      }
    }
    // Guards against a rapid double-click firing two overlapping searches —
    // their fetchCandidatesPage responses could otherwise land out of order
    // and leave the driver list reflecting whichever resolved last rather
    // than the actually-latest filters.
    if (searchInFlightRef.current) return;
    searchInFlightRef.current = true;
    setSearching(true);
    setHasSearched(true); // reveal the driver card's (loading) list immediately
    // Gates the Assign Driver card's Partner filter — a free-text/unmatched
    // hospital name has `id: null` and no real HospitalFleetPartnerships to
    // check against, so only a genuinely selected hospital (a real id) counts.
    const hasHospitalThisSearch = !!formData.hospital?.id;
    setSearchedHospital(hasHospitalThisSearch ? formData.hospital : null);
    // Default the candidate search to whatever Vehicle Type(s) were checked
    // on the booking form — the dispatcher can still broaden/narrow it
    // afterward from the Assign Driver card's own filter. If this search no
    // longer has a hospital, force partnerOnly off too — otherwise a
    // previously-checked Partner filter would survive the hospital being
    // removed, leaving the checkbox stuck checked-but-disabled and the
    // results impossibly empty (partner-only with no partnership data).
    const initialFilters = {
      ...candidateFilters,
      vehicleTypeNames: (formData.vehicleTypes && formData.vehicleTypes.length > 0)
        ? formData.vehicleTypes
        : (formData.vehicleType ? [formData.vehicleType] : []),
      partnerOnly: hasHospitalThisSearch ? candidateFilters.partnerOnly : false,
    };
    setCandidateFilters(initialFilters);
    try {
      const activeBookingId = await ensureBooking(formData);
      const { candidates, total } = await fetchCandidatesPage(activeBookingId, initialFilters, 0);
      setDrivers(candidates);
      setCandidatesTotal(total);
      onAction?.('Nearby ambulances loaded');
    } catch (error) {
      onError?.(error.message || 'Failed to search for nearby ambulances');
    } finally {
      setSearching(false);
      searchInFlightRef.current = false;
    }
  };

  const handleLoadMoreCandidates = async () => {
    const coords = activeGeocodedLocation || pickupCoordinates;
    if ((!bookingId && !coords) || loadingMore) return;
    setLoadingMore(true);
    try {
      const { candidates, total } = await fetchCandidatesPage(
        activeGeocodedLocation ? null : bookingId,
        candidateFilters,
        drivers.length,
        activeGeocodedLocation
      );
      setDrivers((prev) => [...prev, ...candidates]);
      setCandidatesTotal(total);
    } catch (error) {
      onError?.(error.message || 'Failed to load more ambulances');
    } finally {
      setLoadingMore(false);
    }
  };

  // City / Area / Pincode search runs by itself 500ms after the dispatcher stops
  // typing (replaces the old "Find Ambulances" button; Enter still searches
  // immediately). Partial input is ignored so half-typed values don't spend a
  // geocoding call: city/area need 3+ characters, a pincode needs all 6 digits.
  const handleSearchByLocationRef = useRef(handleSearchByLocation);
  handleSearchByLocationRef.current = handleSearchByLocation;
  useEffect(() => {
    const city = locationFilters?.city?.trim() || '';
    const area = locationFilters?.area?.trim() || '';
    const pincode = locationFilters?.pincode?.trim() || '';
    const hasSearchableInput = city.length >= 3 || area.length >= 3 || /^\d{6}$/.test(pincode);
    if (!hasSearchableInput) return undefined;
    const timeoutId = setTimeout(() => handleSearchByLocationRef.current(), 500);
    return () => clearTimeout(timeoutId);
  }, [locationFilters?.city, locationFilters?.area, locationFilters?.pincode]);

  // Re-run the search (from page 1) whenever the ETA/vehicle-type/search
  // filters change, so they narrow the real result set on the server instead
  // of just hiding rows from whatever page happened to already be loaded.
  useEffect(() => {
    const coords = activeGeocodedLocation || pickupCoordinates;
    if (!hasSearched || (!bookingId && (!coords?.latitude || !coords?.longitude))) return undefined;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const { candidates, total } = await fetchCandidatesPage(
          activeGeocodedLocation ? null : bookingId,
          candidateFilters,
          0,
          activeGeocodedLocation
        );
        if (cancelled) return;
        setDrivers(candidates);
        setCandidatesTotal(total);
      } catch (error) {
        if (!cancelled) onError?.(error.message || 'Failed to filter nearby ambulances');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timeoutId); };
    // vehicleTypeNames is compared by its joined contents (a stable
    // primitive), not array reference — a new array literal is created on
    // every seed/update, which would otherwise re-trigger this on every
    // unrelated re-render.
  }, [
    candidateFilters.maxEtaMinutes,
    candidateFilters.maxDistanceKm,
    candidateFilters.vehicleTypeNames.join(','),
    candidateFilters.search,
    candidateFilters.partnerOnly,
    activeGeocodedLocation,
  ]);

  const handleSaveLater = async (formData) => {
    const missing = getMissingRequiredFields(formData, pickupCoordinates, dropoffCoordinates);
    if (missing.length > 0) {
      onError?.(`Please fill in the required fields: ${missing.join(', ')}`);
      return;
    }
    const isSame = isSameLocation || areLocationsSame(
      pickupCoordinates,
      dropoffCoordinates,
      formData?.pickupText,
      formData?.dropoffText
    );
    if (isSame) {
      onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
      return;
    }
    if (formData.scheduleTime) {
      const scheduledTime = new Date(formData.scheduleTime);
      if (scheduledTime < new Date()) {
        onError?.('Scheduled time cannot be in the past. Please select a present or future date and time.');
        return;
      }
    }
    if (saveLaterInFlightRef.current) return;
    saveLaterInFlightRef.current = true;
    setSavingLater(true);
    try {
      await ensureBooking(formData);
      onAction?.('Booking saved for later');
      // Never has a driver at this point, so the outcome is fully known
      // without a fetch — Scheduled if the dispatcher set a schedule time,
      // Pending otherwise.
      onNavigateToTrips?.(formData.scheduleTime ? 'Scheduled' : 'Pending');
    } catch (error) {
      onError?.(error.message || 'Failed to save booking');
    } finally {
      setSavingLater(false);
      saveLaterInFlightRef.current = false;
    }
  };

  const handleUpdateBooking = async (formData) => {
    const missing = getMissingRequiredFields(formData, pickupCoordinates, dropoffCoordinates);
    if (missing.length > 0) {
      onError?.(`Please fill in the required fields: ${missing.join(', ')}`);
      return;
    }
    const isSame = isSameLocation || areLocationsSame(
      pickupCoordinates,
      dropoffCoordinates,
      formData?.pickupText,
      formData?.dropoffText
    );
    if (isSame) {
      onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
      return;
    }
    if (formData.scheduleTime && formData.scheduleTime !== activeBooking?.scheduled_datetime) {
      const scheduledTime = new Date(formData.scheduleTime);
      if (scheduledTime < new Date()) {
        onError?.('Scheduled time cannot be in the past. Please select a present or future date and time.');
        return;
      }
    }
    if (updatingInFlightRef.current) return;
    updatingInFlightRef.current = true;
    setUpdating(true);
    try {
      await ensureBooking(formData);
      onAction?.('Booking updated successfully');
      const targetStage = activeBooking
        ? deriveTripStage(activeBooking)
        : (formData.scheduleTime ? 'Scheduled' : 'Pending');
      onNavigateToTrips?.(targetStage);
    } catch (error) {
      onError?.(error.message || 'Failed to update booking');
    } finally {
      setUpdating(false);
      updatingInFlightRef.current = false;
    }
  };

  // Shared finalize step for every assignment path (list Assign, map marker
  // click, Add & Assign, and Auto Assign once its countdown resolves) — calls
  // the real backend assign/assign-manual endpoint, then updates local state
  // and fills any still-empty booking-form price fields.
  //
  // 'app' is negotiable — the driver still has to accept via their own app,
  // so this doesn't mark them as the assigned driver yet. It opens the
  const isBookingPastDated = () => {
    if (activeBooking?.scheduled_datetime) {
      return new Date(activeBooking.scheduled_datetime).getTime() < Date.now();
    }
    if (activeBooking?.created_at) {
      const ageMs = Date.now() - new Date(activeBooking.created_at).getTime();
      if (ageMs > 24 * 60 * 60 * 1000) return true;
    }
    return false;
  };

  // waiting popup instead, which polls the real booking status and only
  // finalizes once the driver actually confirms (or reports back if they
  // decline/time out).
  const finalizeAssignment = async ({ driver, prices, notifyMethod, callReason }) => {
    // Show the "Registering Driver & Vehicle..." modal immediately (0ms delay) right after Confirm & Assign is clicked!
    if (driver?.isManual) {
      setManualAssignResult({ phase: 'loading' });
    }

    let currentBookingId = bookingId;
    if (!currentBookingId) {
      const formVal = currentFormDataRef.current;
      const missing = getMissingRequiredFields(formVal || {}, pickupCoordinates, dropoffCoordinates);
      if (missing.length > 0) {
        if (driver?.isManual) setManualAssignResult(null);
        onError?.(`Please fill in the required fields to assign a driver: ${missing.join(', ')}`);
        return;
      }
      try {
        currentBookingId = await ensureBooking(formVal);
      } catch (err) {
        if (driver?.isManual) {
          setManualAssignResult({ phase: 'error', errorMessage: err.message || 'Failed to create booking for assignment' });
        } else {
          onError?.(err.message || 'Failed to create booking for assignment');
        }
        return;
      }
    }
    if (!currentBookingId) {
      if (driver?.isManual) setManualAssignResult(null);
      onError?.('No booking to assign a driver to yet');
      return;
    }
    const isSame = isSameLocation || areLocationsSame(
      pickupCoordinates,
      dropoffCoordinates
    );
    if (isSame) {
      if (driver?.isManual) setManualAssignResult(null);
      onError?.('Pickup and drop-off locations cannot be the same. Please select a different drop-off location.');
      return;
    }
    if (isBookingPastDated()) {
      if (driver?.isManual) setManualAssignResult(null);
      onError?.('Driver cannot be assigned to a past-dated booking.');
      return;
    }
    setAssigning(true);
    // Whatever the dispatcher currently has in Offered/Agreed/Partner Price
    // goes with the assignment — prefer the popup's own prices (freshest, and
    // the only place manual/Add & Assign lets them edit at assign time),
    // falling back to the booking form's price fields (auto-assign has none).
    const priceSource = prices || priceFields;
    const priceOverrides = {
      fleetOperatorPrice: (priceSource.fleetOperatorPrice ?? priceSource.offeredPrice) !== '' && (priceSource.fleetOperatorPrice ?? priceSource.offeredPrice) != null ? Number(priceSource.fleetOperatorPrice ?? priceSource.offeredPrice) : undefined,
      platformServiceFee: (priceSource.platformServiceFee ?? priceSource.agreedPrice) !== '' && (priceSource.platformServiceFee ?? priceSource.agreedPrice) != null ? Number(priceSource.platformServiceFee ?? priceSource.agreedPrice) : undefined,
      fleetOperatorServiceFee: (priceSource.fleetOperatorServiceFee ?? priceSource.partnerPrice) !== '' && (priceSource.fleetOperatorServiceFee ?? priceSource.partnerPrice) != null ? Number(priceSource.fleetOperatorServiceFee ?? priceSource.partnerPrice) : undefined,
      totalPrice: priceSource.totalPrice !== '' && priceSource.totalPrice != null ? Number(priceSource.totalPrice) : undefined,
    };
    try {
      let succeeded = true;
      if (driver.isManual) {
        try {
          let manualCoords = null;
          const locParts = [driver.area, driver.city, driver.pincode].filter(Boolean);
          if (locParts.length > 0) {
            try {
              const geoPromise = placesService.geocodeAddress(locParts.join(', '));
              const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 1200));
              const geoRes = await Promise.race([geoPromise, timeoutPromise]);
              if (geoRes?.latitude != null && geoRes?.longitude != null) {
                manualCoords = { latitude: geoRes.latitude, longitude: geoRes.longitude };
              }
            } catch (e) {
              // Geocoding failure should not block assignment
            }
          }

          const result = await commandCenterService.assignManualDriver(currentBookingId, {
            driverName: driver.name,
            driverPhone: driver.phone,
            vehicleType: driver.vehicleType,
            vehiclePlate: driver.vehiclePlate,
            companyName: driver.vendorName,
            city: driver.city,
            area: driver.area,
            pincode: driver.pincode,
            latitude: manualCoords?.latitude,
            longitude: manualCoords?.longitude,
            ...priceOverrides,
          });
          setAssignedDriver(driver);
          setDriverCardExpanded(false);
          setManualAssignResult({ phase: 'success', booking: result.data, driver });
        } catch (manualError) {
          setManualAssignResult({ phase: 'error', errorMessage: manualError.message || 'Failed to assign driver' });
          succeeded = false;
        }
      } else if (notifyMethod === 'app') {
        await commandCenterService.assignDriver(currentBookingId, {
          driverId: driver.driverId,
          vehicleId: driver.vehicleId,
          notifyMethod,
          ...priceOverrides,
        });
        onAction?.(`Request sent to ${driver.name} via the driver app — waiting for them to accept`);
        setWaitingForDriverPopup({ driver });
      } else {
        const result = await commandCenterService.assignDriver(currentBookingId, {
          driverId: driver.driverId,
          vehicleId: driver.vehicleId,
          notifyMethod,
          callReason,
          ...priceOverrides,
        });
        onAction?.(`Booking request sent to ${driver.name} via a call to ${driver.phone} (reason: ${callReason})`);
        setAssignedDriver(driver);
        setDriverCardExpanded(false);
        // Unlike 'app', a call assignment is final the moment it's sent — no
        // accept step to wait on — so the response is already the booking's
        // resulting state.
        onNavigateToTrips?.(deriveTripStage(result.data));
      }
      if (succeeded && prices) setPriceFields((prev) => fillEmptyPriceFields(prev, prices));
    } catch (error) {
      onError?.(error.message || 'Failed to assign driver');
    } finally {
      setAssigning(false);
    }
  };

  const openDriverAssignPopup = (driver) => {
    if (isBookingPastDated()) {
      onError?.('Driver cannot be assigned to a past-dated booking.');
      return;
    }
    setPricingPopup({ mode: 'driver', driver });
  };
  const openManualAssignPopup = () => {
    if (isBookingPastDated()) {
      onError?.('Driver cannot be assigned to a past-dated booking.');
      return;
    }
    setPricingPopup({ mode: 'manual' });
  };
  const openAutoAssignPopup = async () => {
    if (drivers.length === 0) return;
    if (isBookingPastDated()) {
      onError?.('Driver cannot be assigned to a past-dated booking.');
      return;
    }
    if (!bookingId) {
      const formVal = currentFormDataRef.current;
      const missing = getMissingRequiredFields(formVal || {}, pickupCoordinates, dropoffCoordinates);
      if (missing.length > 0) {
        onError?.(`Please fill in the required fields to auto-assign: ${missing.join(', ')}`);
        return;
      }
      try {
        await ensureBooking(formVal);
      } catch (err) {
        onError?.(err.message || 'Failed to create booking for auto-assign');
        return;
      }
    }
    setAutoAssignPopup(true);
  };

  if (loadingEditBooking) {
    return (
      <div className="bg-white rounded-2xl shadow p-12 flex items-center justify-center gap-2 text-sm text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading booking…
      </div>
    );
  }

  const currentTripStage = activeBooking ? deriveTripStage(activeBooking) : null;
  const isActiveTrip = Boolean(
    activeBooking &&
    ['Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop'].includes(currentTripStage)
  );

  return (
    <div className="space-y-3 pb-6">
      {editBookingId && (
        <button
          type="button"
          onClick={() => onNavigateToTrips?.()}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Trips
        </button>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <CommandCenterBookingForm
        key={editBookingId || 'new'}
        initialFormData={editInitialFormData}
        onPickupCoordinatesChange={handlePickupCoordinatesChange}
        onDropoffCoordinatesChange={setDropoffCoordinates}
        distanceKm={route.distanceKm}
        durationMin={route.durationMin}
        isSameLocation={isSameLocation}
        priceFields={priceFields}
        onPriceFieldChange={(key, value) => setPriceFields((prev) => ({ ...prev, [key]: value }))}
        calculatingPrice={calculatingPrice}
        onVehicleTypeChange={handleVehicleTypeChange}
        onAction={onAction}
        onError={onError}
        onSaveLater={handleSaveLater}
        onSearchAmbulance={handleSearchAmbulance}
        onFormDataChange={(val) => {
          currentFormDataRef.current = val;
          const text = val?.pickupText?.trim();

          if (!text) {
            if (!pickupCoordinates) {
              setLocationFilters({ city: '', area: '', pincode: '' });
            }
            return;
          }

          // 1. Instant 6-digit pin code extraction
          const pinMatch = text.match(/\b(\d{6})\b/);
          if (pinMatch) {
            setLocationFilters((prev) => (prev.pincode === pinMatch[1] ? prev : { ...prev, pincode: pinMatch[1] }));
          }

          // 2. Clear previous geocode timer
          if (pickupGeocodeTimerRef.current) {
            clearTimeout(pickupGeocodeTimerRef.current);
          }

          // Skip if already matching current selected pickup coordinates address or name
          const alreadySelected = pickupCoordinates && (
            text === (pickupCoordinates.name || '').trim() ||
            text === (pickupCoordinates.address || '').trim()
          );
          if (alreadySelected) return;

          // 3. Debounced geocode to auto-extract city, area, pincode
          if (text.length >= 3) {
            pickupGeocodeTimerRef.current = setTimeout(async () => {
              try {
                const res = await placesService.geocodeAddress(text);
                if (res?.success && res.data) {
                  const parsed = extractAddressDetails(
                    res.data.addressComponents,
                    res.data.formattedAddress,
                    res.data.name
                  );
                  if (parsed.city || parsed.area || parsed.pincode) {
                    setLocationFilters((prev) => ({
                      city: parsed.city || prev.city || '',
                      area: parsed.area || prev.area || '',
                      pincode: parsed.pincode || (pinMatch ? pinMatch[1] : prev.pincode || ''),
                    }));
                  }
                }
              } catch (_) {
                // Graceful fallback
              }
            }, 500);
          }
        }}
        searching={searching}
        savingLater={savingLater}
        isEditMode={Boolean(editBookingId)}
        isActiveTrip={isActiveTrip}
        tripStage={currentTripStage}
        onUpdateBooking={handleUpdateBooking}
        updating={updating}
        bookingId={bookingId || editBookingId || null}
        onLocationShareIdChange={(shareId) => { locationShareIdRef.current = shareId; }}
      />

      <div className="lg:sticky lg:top-0 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto space-y-4">
        <CommandCenterAssignDriverCard
          drivers={hasSearched ? drivers : []}
          total={candidatesTotal}
          hasSearched={hasSearched}
          searching={searching}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMoreCandidates}
          filters={candidateFilters}
          onFiltersChange={(patch) => setCandidateFilters((prev) => ({ ...prev, ...patch }))}
          locationFilters={locationFilters}
          onLocationFiltersChange={(patch) => setLocationFilters((prev) => ({ ...prev, ...patch }))}
          onSearchByLocation={handleSearchByLocation}
          activeLocationSearch={activeGeocodedLocation}
          onClearLocationSearch={handleClearLocationSearch}
          isGeocoding={isGeocoding}
          hasHospital={!!searchedHospital}
          selectedDriverId={assignedDriver?.id ?? null}
          assignedDriver={assignedDriver}
          expanded={driverCardExpanded}
          onToggleExpanded={() => setDriverCardExpanded((prev) => !prev)}
          onRequestAssign={openDriverAssignPopup}
          onRequestManualAssign={openManualAssignPopup}
          onRequestAutoAssign={openAutoAssignPopup}
          assigning={assigning}
        />
        <CommandCenterBookingMapCard
          pickupCoordinates={pickupCoordinates}
          dropoffCoordinates={dropoffCoordinates}
          drivers={assignedDriver ? [assignedDriver] : (hasSearched ? drivers : EMPTY_DRIVERS_LIST)}
          selectedDriverId={assignedDriver?.id ?? null}
          onDriverMarkerClick={assignedDriver || assigning ? undefined : openDriverAssignPopup}
          routePath={route.path}
          distanceKm={route.distanceKm}
          durationMin={route.durationMin}
        />
      </div>
      </div>

      {pricingPopup && (
        <CommandCenterAssignPricingPopup
          mode={pricingPopup.mode}
          driver={pricingPopup.driver ?? null}
          priceFields={priceFields}
          initialLocation={locationFilters}
          assigning={assigning}
          onClose={() => setPricingPopup(null)}
          onError={onError}
          onAssign={(payload) => {
            finalizeAssignment(payload);
            setPricingPopup(null);
          }}
        />
      )}

      {autoAssignPopup && (
        <CommandCenterAutoAssignPopup
          bookingId={bookingId}
          filters={candidateFilters}
          onClose={() => setAutoAssignPopup(false)}
          onConfirmed={(booking) => {
            setAssignedDriver(mapConfirmedBookingToDriver(booking));
            setDriverCardExpanded(false);
            onAction?.(`${booking.driver?.name || 'Driver'} confirmed the booking via Auto Assign`);
          }}
          onExhausted={() => {
            onError?.('No nearby drivers accepted — please choose one manually or try Add & Assign');
          }}
          onDone={onNavigateToTrips}
        />
      )}

      {waitingForDriverPopup && (
        <CommandCenterWaitingForDriverPopup
          bookingId={bookingId}
          driver={waitingForDriverPopup.driver}
          onClose={() => setWaitingForDriverPopup(null)}
          onConfirmed={() => {
            setAssignedDriver(waitingForDriverPopup.driver);
            setDriverCardExpanded(false);
            onAction?.(`${waitingForDriverPopup.driver.name} confirmed the booking`);
          }}
          onDeclined={() => {
            onError?.(`${waitingForDriverPopup.driver.name} declined or didn't respond — please choose another driver`);
          }}
          onDone={onNavigateToTrips}
        />
      )}

      {manualAssignResult && (
        <CommandCenterManualAssignPopup
          phase={manualAssignResult.phase}
          booking={manualAssignResult.booking}
          driver={manualAssignResult.driver}
          errorMessage={manualAssignResult.errorMessage}
          onClose={() => setManualAssignResult(null)}
          onDone={onNavigateToTrips}
        />
      )}
    </div>
  );
};

export default CommandCenterBookingPage;
